import { ApolloClient, CombinedGraphQLErrors, HttpLink, InMemoryCache } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { Observable } from 'rxjs';

const GRAPHQL_ENDPOINT = '/graphql';

const REFRESH_MUTATION = `
  mutation RefreshSession {
    refreshSession {
      authenticated
      user { id }
    }
  }
`;

/**
 * Callback fired when the session cannot be recovered.
 *
 * Set by the auth provider rather than imported, so this module stays free of
 * router and store dependencies and can be used in tests.
 */
let onSessionLost: (() => void) | undefined;
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/**
 * Refreshes at most once at a time.
 *
 * A dashboard fires half a dozen queries on load; without this they would each
 * rotate the refresh cookie and the losers would be rejected as replays by the
 * backend's stolen-token detection.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      // Apollo Server's CSRF prevention rejects requests that a form could have
      // produced; a non-standard header proves this went through fetch.
      'apollo-require-preflight': 'true',
    },
    body: JSON.stringify({ query: REFRESH_MUTATION }),
  })
    .then(async (response) => {
      if (!response.ok) return false;
      const payload = (await response.json()) as {
        data?: { refreshSession?: { authenticated?: boolean } };
      };
      return Boolean(payload.data?.refreshSession?.authenticated);
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function isUnauthenticated(error: unknown): boolean {
  return (
    CombinedGraphQLErrors.is(error) &&
    error.errors.some((entry) => entry.extensions?.code === 'UNAUTHENTICATED')
  );
}

const authRecoveryLink = new ErrorLink(({ error, operation, forward }) => {
  if (!isUnauthenticated(error)) return;

  // Refreshing a refresh is a loop; so is refreshing a login attempt.
  const name = operation.operationName;
  if (name === 'RefreshSession' || name === 'Login' || name === 'Register') return;

  return new Observable((subscriber) => {
    let cancelled = false;

    void refreshSession().then((recovered) => {
      if (cancelled) return;
      if (!recovered) {
        onSessionLost?.();
        subscriber.error(error);
        return;
      }
      forward(operation).subscribe(subscriber);
    });

    return () => {
      cancelled = true;
    };
  });
});

const httpLink = new HttpLink({
  uri: GRAPHQL_ENDPOINT,
  // The session lives in httpOnly cookies, so every request has to carry them.
  credentials: 'include',
  headers: { 'apollo-require-preflight': 'true' },
});

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Both are keyed by an argument the caller always supplies, so the
        // default "one slot per field" policy would thrash between restaurants.
        menu: { keyArgs: ['menuId'] },
        menuInstance: { keyArgs: ['id'] },
        renderMenuInstance: { keyArgs: ['id'] },
        publicMenu: { keyArgs: ['slug'] },
        templateVersion: { keyArgs: ['versionId'] },
      },
    },
    Menu: {
      fields: {
        // Categories are embedded documents without stable normalisation, so
        // an incoming list replaces the old one instead of being merged.
        categories: { merge: false },
      },
    },
    MenuCategory: {
      fields: { items: { merge: false } },
    },
    RenderedMenu: {
      // Prefer revisionId when present (guest); revision alone for live owner previews.
      keyFields: ['instanceId', 'revision'],
    },
  },
});

/**
 * Apollo 4 requires defaults to be declared before they can be set, so that a
 * component reading `data` knows whether it can be partial.
 *
 * `all` is right for this app: a page with one failing field should render the
 * rest rather than collapse into an error state.
 */
declare module '@apollo/client' {
  namespace ApolloClient.DeclareDefaultOptions {
    interface WatchQuery {
      errorPolicy: 'all';
    }
    interface Query {
      errorPolicy: 'all';
    }
  }
}

export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    link: authRecoveryLink.concat(httpLink),
    cache,
    devtools: { enabled: import.meta.env.DEV },
    defaultOptions: {
      watchQuery: { errorPolicy: 'all' },
      query: { errorPolicy: 'all' },
    },
  });
}

export const apolloClient = createApolloClient();
